import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  IntakeSectionHeader,
  StyledGridCol,
  StyledGridRow,
} from "@/common/styles";
import { FormContainer } from "../InmateDetails/styles";
import SecureFormItem from "@/common/SecureFormItem";
import { getOptionsFromRefList } from "@/util/common_function";
import { useSelector } from "react-redux";
import { SCHEMAS } from '@/constants/schemas';

// Warrant Number applies to these custody reason IDs
const WARRANT_REASON_IDS = [
  54180010, 54180009, 54180011, 54180013, 54180007, 54180008,
  54180026, 54180020, 54180021, 54180019, 54180018, 54180023,
  54180022, 54180006, 54180016, 54180003, 54180017, 54180024,
];

// Ticket Number applies to these custody reason IDs
const TICKET_REASON_IDS = [54180007, 54180006, 54180002];

const ChargeInformation = ({ originalCustodyReasonID, startingPoint="" }) => {
  const fullSearch = useSelector((state) => state.fullSearch);

  const checkCount = (_, value) => {
    if (value > 0 && value <= 10000) return Promise.resolve();
    return Promise.reject(new Error("Count must be greater than 0 and less than 10000"));
  };

  // NOTE: No <FieldSecurityProvider> needed here — this component is always
  // rendered inside <AddCharges> which already provides entity="Charges".
  // SecureFormItem reads the context automatically.
  return (
    <>
      <IntakeSectionHeader>
        <span>Charge Information</span>
      </IntakeSectionHeader>

      <FormContainer>
        <StyledGridRow gutter={16}>

          {/* Charge */}
          <StyledGridCol span={16}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.CHARGE_DESCRIPTION_ID}
              formItemProps={{
                name: "chargeDescriptionID",
                label: "Charge",
                required: !fullSearch,
                rules: !fullSearch
                  ? [{ required: true, message: "Charge is required" }]
                  : [],
              }}
            >
              <Select
                options={getOptionsFromRefList("Charges", false, false, true)}
                placeholder="Charge"
                isSelectedValueFocus
                variant="outlined"
                showSearch
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* UCR Code — always read-only, overrideDisabled forces it disabled */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.UCR_CODE}
              overrideDisabled
              formItemProps={{ name: "ucrCode", label: "Code" }}
            >
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          {/* Charge Level */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.CHARGE_LEVEL_ID}
              formItemProps={{
                name: "chargeLevelID",
                label: "Level",
                required: !fullSearch,
                rules: !fullSearch
                  ? [{ required: true, message: "Charge level is required" }]
                  : [],
              }}
            >
              <Select
                options={getOptionsFromRefList("Charge_Level", false, false, true)}
                placeholder="Level"
                variant="outlined"
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Charge Count */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.CHARGE_COUNT}
              formItemProps={{
                name: "chargeCount",
                label: "Count",
                rules: !fullSearch ? [{ validator: checkCount }] : [],
              }}
            >
              <TextField type="number" />
            </SecureFormItem>
          </StyledGridCol>

          {/* Charge Modifier */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.CHARGE_MODIFIER_ID}
              formItemProps={{ name: "chargeModifierID", label: "Charge Modifier" }}
            >
              <Select
                options={getOptionsFromRefList("Charge_Modifier", false, false, true)}
                placeholder="Charge Modifier"
                variant="outlined"
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Controlling Agency */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.CONTROLLING_AGENCY_ORI_ID}
              formItemProps={{
                name: "controllingAgencyORIID",
                label: "Controlling Agency",
                required: !fullSearch,
                rules: !fullSearch
                  ? [{ required: true, message: "Controlling agency is required" }]
                  : [],
              }}
            >
              <Select
                options={getOptionsFromRefList("Controlling_Agency", false, true, true)}
                placeholder="Controlling Agency"
                variant="outlined"
                showSearch
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Case Number */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.CASE_NUMBER}
              formItemProps={{
                name: "caseNumber",
                label: "Case Number",
                required: !fullSearch,
                rules: !fullSearch
                  ? [
                      { required: true, message: "Case number is required" },
                      {
                        pattern: /^([A-Za-z]{2}\d{2}-\d{6}|\d{8})$/i,
                        message:
                          "The Case Number must be either 'aa##-######' (11 chars) or ######## (8 chars)",
                      },
                    ]
                  : [],
              }}
            >
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          {/* Court Number */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.COURT_NUMBER}
              formItemProps={{ name: "courtNumber", label: "Court Number" }}
            >
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          {/* Original Custody Reason */}
          <StyledGridCol span={6}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.ORIGINAL_CUSTODY_REASON_ID}
              formItemProps={{
                name: "originalCustodyReasonID",
                label: "Original Custody Reason",
                required: !fullSearch,
                rules: !fullSearch
                  ? [{ required: true, message: "Original custody reason is required" }]
                  : [],
              }}
            >
              <Select
                options={getOptionsFromRefList("Reason_Held", false, false, true)}
                placeholder="Original Custody Reason"
                variant="outlined"
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Warrant Number — conditional on custody reason */}
          {WARRANT_REASON_IDS.includes(originalCustodyReasonID) && (
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName={SCHEMAS.CHARGES.FIELDS.WARRANT_NUMBER}
                formItemProps={{ name: "warrantNumber", label: "Warrant Number" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          )}

          {/* Ticket Number — conditional on custody reason */}
          {TICKET_REASON_IDS.includes(originalCustodyReasonID) && (
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName={SCHEMAS.CHARGES.FIELDS.TICKET_NUMBER}
                formItemProps={{ name: "ticketNumber", label: "Ticket Number" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          )}

          {/* Comments */}
          <StyledGridCol span={24}>
            <SecureFormItem
              fieldName={SCHEMAS.CHARGES.FIELDS.COMMENTS}
              formItemProps={{ name: "comments", label: "Comments" }}
            >
              <TextField inputType="textarea" rows={5} />
            </SecureFormItem>
          </StyledGridCol>

        </StyledGridRow>

        {/* Empty rows preserved from original */}
        <StyledGridRow gutter={16} />
        <StyledGridRow gutter={16} />
      </FormContainer>
    </>
  );
};

export default ChargeInformation;